/**
 * EaaCategorySection Component
 *
 * Displays a collapsible section for grouping EAA issues by category.
 * Shows category icon, name, issue count, and severity breakdown.
 */

import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Keyboard,
  Ear,
  Focus,
  Palette,
  Heading1,
  Tags,
  Link2,
  MousePointerClick,
  FormInput,
  Video,
  AlertTriangle,
  MapPin,
  Smartphone,
  Cookie,
} from 'lucide-react';
import type { EaaCategory } from './EaaFilters';

type Severity = 'critical' | 'serious' | 'moderate' | 'minor';

interface CategoryConfig {
  icon: typeof Keyboard;
  label: string;
  description: string;
}

const CATEGORY_CONFIG: Record<EaaCategory, CategoryConfig> = {
  'keyboard-navigation': {
    icon: Keyboard,
    label: 'Keyboard Navigation',
    description: 'Tab order, keyboard focus, and keyboard operability',
  },
  'screen-reader-support': {
    icon: Ear,
    label: 'Screen Reader Support',
    description: 'Landmarks, alternative text, and ARIA live regions',
  },
  'focus-management': {
    icon: Focus,
    label: 'Focus Management',
    description: 'Modal focus, hidden content, and focus indicators',
  },
  'color-contrast': {
    icon: Palette,
    label: 'Color Contrast',
    description: 'Text contrast, interactive element contrast, and color indicators',
  },
  'headings-structure': {
    icon: Heading1,
    label: 'Headings & Structure',
    description: 'Heading hierarchy, semantic headings, and page structure',
  },
  'aria-semantics': {
    icon: Tags,
    label: 'ARIA & Semantics',
    description: 'ARIA roles, states, properties, and semantic HTML',
  },
  'links-buttons': {
    icon: Link2,
    label: 'Links & Buttons',
    description: 'Link text, button labels, and new tab indicators',
  },
  'interactive-elements': {
    icon: MousePointerClick,
    label: 'Interactive Elements',
    description: 'Touch targets, toggles, tabs, carousels, and dropdowns',
  },
  forms: {
    icon: FormInput,
    label: 'Forms',
    description: 'Form labels, error handling, and required field indication',
  },
  media: {
    icon: Video,
    label: 'Media',
    description: 'Video captions, transcripts, and media controls',
  },
  'common-pitfalls': {
    icon: AlertTriangle,
    label: 'Common Pitfalls',
    description: 'Tabindex issues, placeholder labels, and auto-refresh',
  },
  'skip-links-landmarks': {
    icon: MapPin,
    label: 'Skip Links & Landmarks',
    description: 'Skip navigation, landmark regions, and page navigation',
  },
  'mobile-responsive': {
    icon: Smartphone,
    label: 'Mobile & Responsive',
    description: 'Hamburger menus, touch targets, and mobile navigation',
  },
  'cookies-consent': {
    icon: Cookie,
    label: 'Cookies & Consent',
    description: 'Cookie banner accessibility, focus management, and controls',
  },
};

interface SeverityCount {
  critical: number;
  serious: number;
  moderate: number;
  minor: number;
}

interface EaaCategorySectionProps {
  category: EaaCategory;
  issueCount: number;
  severityCounts: SeverityCount;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

function SeverityDot({ severity, count }: { severity: Severity; count: number }) {
  if (count === 0) return null;

  const colors: Record<Severity, string> = {
    critical: 'bg-red-500',
    serious: 'bg-orange-500',
    moderate: 'bg-yellow-500',
    minor: 'bg-blue-500',
  };

  return (
    <div className="flex items-center gap-1" title={`${count} ${severity}`}>
      <span className={`w-2 h-2 rounded-full ${colors[severity]}`} />
      <span className="text-caption text-gray-medium">{count}</span>
    </div>
  );
}

export function EaaCategorySection({
  category,
  issueCount,
  severityCounts,
  defaultExpanded = false,
  children,
}: EaaCategorySectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const config = CATEGORY_CONFIG[category];
  const Icon = config.icon;

  // Determine border color based on highest severity
  const getBorderColor = () => {
    if (severityCounts.critical > 0) return 'border-l-red-500';
    if (severityCounts.serious > 0) return 'border-l-orange-500';
    if (severityCounts.moderate > 0) return 'border-l-yellow-500';
    if (severityCounts.minor > 0) return 'border-l-blue-500';
    return 'border-l-gray-300';
  };

  return (
    <div className={`border border-gray-light rounded-lg overflow-hidden border-l-4 ${getBorderColor()}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-light/30 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="text-coral flex-shrink-0">
            <Icon size={20} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-body font-semibold text-dark-text">{config.label}</h4>
              <span className="text-body-sm font-medium text-coral bg-coral/10 px-2 py-0.5 rounded">
                {issueCount} {issueCount === 1 ? 'issue' : 'issues'}
              </span>
            </div>
            <p className="text-caption text-gray-medium truncate">{config.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-shrink-0 ml-4">
          {/* Severity breakdown */}
          <div className="hidden sm:flex items-center gap-3">
            <SeverityDot severity="critical" count={severityCounts.critical} />
            <SeverityDot severity="serious" count={severityCounts.serious} />
            <SeverityDot severity="moderate" count={severityCounts.moderate} />
            <SeverityDot severity="minor" count={severityCounts.minor} />
          </div>

          {/* Expand/collapse icon */}
          {isExpanded ? (
            <ChevronDown size={20} className="text-gray-medium" />
          ) : (
            <ChevronRight size={20} className="text-gray-medium" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-light p-4 bg-gray-light/10">
          {children}
        </div>
      )}
    </div>
  );
}

export { CATEGORY_CONFIG };
export default EaaCategorySection;
